import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText, Row } from "@/components";
import { GeneralMediaContainer } from "@/components/companies/company-intake/general-media/GeneralMediaContainer";
import { AutosaveFormEnumMultiselect } from "@/components/companies/shared/autosave-form/AutosaveFormEnumMultiselect";
import { AutosaveFormLinkInput } from "@/components/companies/shared/autosave-form/AutosaveFormLinkInput";
import { AutosaveFormRichTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormRichTextInput";
import { AutosaveFormTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormTextInput";
import { IntakeForm } from "@/components/companies/shared/IntakeForm";
import { ReadOnlyField, ReadOnlyGeneralMediaField, ReadOnlyLinkField, ReadOnlyRichTextField } from "@/components/companies/shared/ReadOnlyField";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useUpdateSetCompanyIntake } from "@/hooks/updateSets/useUpdateSetCompanyIntake";
import {
  companyIndustryOptions,
  companyIndustryToLabel,
} from "@/lib/constants";
import { media } from "@/lib/media";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CompanyIndustry, CompanyIntake, MediaLinkType } from "@/types";

const mediaLinkSchema = z.object({
  id: z.string(),
  companyFileId: z.number().nullable(),
  title: z.string().nullable(),
  type: z.nativeEnum(MediaLinkType),
  url: z.union([z.string().url(), z.literal("")]).nullable(),
});

const generalInfoSchema = z.object({
  websiteUrl: z.union([z.string().url(), z.literal("")]).nullable(),
  industries: z.array(z.nativeEnum(CompanyIndustry)).nullable(),
  headCount: z.string().nullable(),
  whatTheyMake: z.string().nullable(),
  rawMaterials: z.string().nullable(),
  equipment: z.string().nullable(),
  manufacturingProcess: z.string().nullable(),
  maintenanceEnvironment: z.string().nullable(),
  customers: z.string().nullable(),
  standards: z.string().nullable(),
  mediaLinks: z.array(mediaLinkSchema),
});

export const GeneralInfo = ({
  companyIntake,
  isEditing,
}: {
  companyIntake: CompanyIntake;
  isEditing: boolean;
}) => {
  /* Database Operations */
  // Updates
  const { mutate: updateCompanyIntake, isError } = useUpdateSetCompanyIntake();

  /* Misc Hooks */
  const currentCompanyId = useCurrentCompanyId();
  const form = useForm<z.infer<typeof generalInfoSchema>>({
    resolver: zodResolver(generalInfoSchema),
    defaultValues: companyIntake.general,
  });

  /* React Hooks */
  // Ensure all photos are loaded with signed URLs
  useEffect(() => {
    const mediaLinks = form.getValues("mediaLinks");
    void media
      .resolveMediaLinkSignedUrls(mediaLinks, currentCompanyId)
      .then((resolved) => {
        form.setValue("mediaLinks", resolved);
      });
  }, [form, currentCompanyId]);

  /* Handlers */
  const handleSave = (data: z.infer<typeof generalInfoSchema>) => {
    mixpanelUtils.trackFormSubmission("General Info intake", MIXPANEL_SCREENS.COMPANY);
    updateCompanyIntake({
      updateCompanyIntakeRequest: {
        general: data,
      },
    });
  };

  const saveOnBlur = () => {
    void form.handleSubmit(handleSave)();
  };

  /* Helpers */
  const general = companyIntake.general;
  const industriesLabel =
    general.industries && general.industries.length > 0
      ? general.industries.map((i) => companyIndustryToLabel[i]).join(", ")
      : null;

  /* Render */
  const render = () => {
    // Editing view
    if (isEditing) {
      return (
        <IntakeForm form={form} onSave={saveOnBlur}>
          <Column className="gap-6">
            <Row className="gap-4">
              <AutosaveFormEnumMultiselect
                className="flex-1"
                enumToLabel={companyIndustryToLabel}
                fieldName="industries"
                form={form}
                label="Industries (select max 2)"
                maxSelections={2}
                onSelect={saveOnBlur}
                options={companyIndustryOptions}
              />

              <AutosaveFormTextInput
                className="flex-1"
                fieldName="headCount"
                form={form}
                label="Head Count"
                onBlur={saveOnBlur}
                placeholder="e.g., 150 or 100-200"
              />
            </Row>

            <AutosaveFormLinkInput
              fieldName="websiteUrl"
              form={form}
              label="Company Website"
              onBlur={saveOnBlur}
              placeholder="https://example.com"
            />

            <GeneralMediaContainer
              fieldName="mediaLinks"
              form={form}
              onSave={saveOnBlur}
            />

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="whatTheyMake"
              form={form}
              infoTooltip={
                "What exactly do they manufacture?\nWhat does that thing do (if not obvious)?\nAre there any pictures attached relating to what they make?"
              }
              label="What They Make"
              onBlur={saveOnBlur}
            />

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="rawMaterials"
              form={form}
              infoTooltip={
                "What are the raw materials seen at this plant?\nDoes the starting state of these materials suggest initial processing is done at this site or elsewhere?\nAre there any pictures attached showing the materials at the start of production?"
              }
              label="Raw Materials and Starting Point"
              onBlur={saveOnBlur}
            />

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="equipment"
              form={form}
              infoTooltip={
                "What are the primary types of machines or tools used to create the product?\nWhat is the balance of automation versus manual operation on the floor?\nAre there specific control systems or technologies driving the machinery?"
              }
              label="Equipment"
              onBlur={saveOnBlur}
            />

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="manufacturingProcess"
              form={form}
              infoTooltip={
                "What are the major stages of production from raw material to finished good?\nIs this primarily a continuous process (like chemicals or paper) or discrete manufacturing (like individual parts or electronics)?\nHow does the product physically move between stages?\nAt what points in the process are quality checks or testing performed?"
              }
              label="Manufacturing Process"
              onBlur={saveOnBlur}
            />

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="maintenanceEnvironment"
              form={form}
              infoTooltip={
                "What machines/processes do they use?\nWhy is it done this way?\nHow is that reflected in leadership style?"
              }
              label="Maintenance Environment"
              onBlur={saveOnBlur}
            />

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="customers"
              form={form}
              infoTooltip="Who are their customers?"
              label="Customers"
              onBlur={saveOnBlur}
            />

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="standards"
              form={form}
              infoTooltip={
                "What official industry certifications or regulatory standards does the facility hold?\nHow tight are the manufacturing tolerances or quality expectations?\nAre there specific environmental, safety, or cleanliness requirements on the floor?\nDo they have strict customer-driven compliance or audit requirements?"
              }
              label="Standards They Adhere To"
              onBlur={saveOnBlur}
            />

            {isError && (
              <ErrorText errorMessage="An error occurred while saving. Please try again." />
            )}
          </Column>
        </IntakeForm>
      );
    }

    // Read only view
    return (
      <Column className="gap-4">
        <Row className="gap-4">
          <ReadOnlyField
            className="flex-1"
            label="Industries"
            value={industriesLabel}
          />
          <ReadOnlyField
            className="flex-1"
            label="Head Count"
            value={general.headCount}
          />
        </Row>
        <ReadOnlyLinkField label="Company Website" url={general.websiteUrl} />
        <ReadOnlyGeneralMediaField mediaLinks={form.watch("mediaLinks")} />
        <ReadOnlyRichTextField
          label="What They Make"
          value={general.whatTheyMake}
        />
        <ReadOnlyRichTextField
          label="Raw Materials and Starting Point"
          value={general.rawMaterials}
        />
        <ReadOnlyRichTextField label="Equipment" value={general.equipment} />
        <ReadOnlyRichTextField
          label="Manufacturing Process"
          value={general.manufacturingProcess}
        />
        <ReadOnlyRichTextField
          label="Maintenance Environment"
          value={general.maintenanceEnvironment}
        />
        <ReadOnlyRichTextField label="Customers" value={general.customers} />
        <ReadOnlyRichTextField
          label="Standards They Adhere To"
          value={general.standards}
        />
      </Column>
    );
  };

  return render();
};
